import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  ElementRef
} from '@angular/core';

import { QueryService } from '../../../../services/query/query.service';

import {
  Dimension,
  Schema
} from '../../../../models/analytics-api/schema.model';

import { Query } from '../../../../models/analytics-api/query.model';
import { QueryFilterOption } from '../../../../models/query-option.model';

import { IQueryFilterWrapper, IQueryOptions } from '../../../../interfaces/query-filter.interface';
import { QUERY_GLOBAL_OPTION_FILTERS, QUERY_WIDGET_OPTION_FILTERS } from '../../../../constants/query-filter.constants';
import { TrackingService } from '../../../../services/tracking/tracking.service';
import { FilterSource } from '../../../../models/filter-source.model';

@Component({
  selector: 'query-builder-modal',
  templateUrl: './query-builder-modal.component.html',
  styleUrls: ['./query-builder-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class QueryBuilderModalComponent implements OnInit, AfterViewInit {
  @Input() query: Query;
  @Input() schema: Schema;
  @Output() modalFiltersClosed = new EventEmitter<object>();

  public $element: any;
  public datasetName: string;
  public interval: string;
  public filters: IQueryFilterWrapper[];
  public widgetFilterWarnings: any[] = [];
  public globalSelectedFilterSources: FilterSource[];
  public querySelectedFilterSources: FilterSource[];
  public queryFilterOptions: QueryFilterOption[] = [];
  public globalFilterOptions: QueryFilterOption[] = [];
  public queryOptions: IQueryOptions;
  public globalOptions: IQueryOptions;
  public globalFilters: IQueryFilterWrapper[];
  public pageSpecificFilters: IQueryFilterWrapper[];
  public emptyFilterTypeExists: boolean = false;

  private queryDimensions: Dimension[];
  private filterSourceList: FilterSource[];
  private updatedQueryFilters: IQueryFilterWrapper[];

  constructor(
    private queryService: QueryService,
    private elementRef: ElementRef,
    private trackingService: TrackingService
  ) {}

  public ngOnInit(): void {
    this.datasetName = this.query.dataset;
    this.filters = this.query.filters || [];
    this.filterSourceList = this.queryService.getFilterSourceList(this.schema.dimensions);
    this.queryDimensions = this.query.dimensions;
    this.interval = this.query.interval;
    this.globalSelectedFilterSources = this.checkGlobalSelectedFilterSources();
    this.querySelectedFilterSources = this.checkQuerySelectedFilterSources();
    this.queryOptions = this.setQueryOptions(true, true, true);
    this.globalOptions = this.setQueryOptions(false, false, false);
    this.queryFilterOptions = QUERY_WIDGET_OPTION_FILTERS;
    this.globalFilterOptions = QUERY_GLOBAL_OPTION_FILTERS;
    this.globalFilters = this.query.globalFilters.filter(filter => filter.pageId === undefined);
    this.pageSpecificFilters = this.query.globalFilters.filter(filter => filter.pageId !== undefined);
  }

  public updateFilters(data) {
    this.updatedQueryFilters = data.filters;
    this.emptyFilterTypeExists = data.emptyFilterTypeWarnings.flatten().find(value => value === true);
  }

  private setQueryOptions(
    showMultipleOptions: boolean,
    showOrFilters: boolean,
    showEmptyFilterWarning: boolean
  ): IQueryOptions {
    return {
      showMultipleOptions,
      showOrFilters,
      showEmptyFilterWarning
    }
  }

  public applyFilters(): void {
    this.trackingService.track('Add advanced filter and run query')
    const runQuery = this.hasMetrics;
    if (!!this.updatedQueryFilters) {
      this.query.filters = this.updatedQueryFilters;
      this.query.filters.forEach(filter => {
        filter['expressions'].forEach(expression => {
          if (expression && expression['type']) {
            const filterType = this.queryFilterOptions.find(qfo => qfo['type'] === expression['type'])
            if (filterType) {
              this.trackingService.track(`Apply advanced filter`, {value: filterType['name']})
            }
          }
        })
      })
      this.modalFiltersClosed.emit({runQuery: runQuery});
    }
  }

  public cancel(): void {
    this.modalFiltersClosed.emit({ runQuery: false });
  }

  public get hasMetrics(): boolean {
    return !!this.query.metrics.length;
  }

  private checkGlobalSelectedFilterSources(): FilterSource[] {
    const globalSelected = this.query.globalFilters.reduce((filterTotal: any[], filter: any) => {
      filterTotal.push(this.queryService.findFilterSource(filter.expressions[0], this.filterSourceList));
      return filterTotal;
    }, []);

    const totalGlobalSelected = Array.from(new Set([...globalSelected, ...this.queryDimensions]));
    return totalGlobalSelected.flatMap(selected => new FilterSource(selected));
  }

  private checkQuerySelectedFilterSources(): FilterSource[] {
    const querySelected = this.query.filters.reduce((filterTotal: any[], filter: any) => {
      filterTotal.push(this.queryService.findFilterSource(filter.expressions[0], this.filterSourceList));
      return filterTotal;
    }, []);

    const querySelectedFiltered = Array.from(new Set([...querySelected]))
    return querySelectedFiltered.flatMap(filtered => new FilterSource(filtered));
  }

  public ngAfterViewInit() {
    this.$element = $(this.elementRef.nativeElement).find('.ui.accordion')
    this.$element.accordion();
  }

  public trackFilterOpen(isPage: boolean, event) {
    const isActive: boolean = event.srcElement.classList.contains('active');
    const msg = `${isActive ? 'Open' : 'Close'} ${isPage ? 'page specific' : 'global'} filters in widget filters modal`;
    this.trackingService.track(msg);
  }

}
